import {
  Autocomplete,
  AutocompleteRenderGroupParams,
  Box,
  BoxProps,
  ListItemText,
  ListSubheader,
  TextField,
} from '@mui/material';
import { HTMLAttributes, forwardRef, useCallback, useEffect, useMemo } from 'react';

import { useGetDeliveryRouteByIdQuery } from '@api/deliveryRoutes';
import { useGetLocationsApiListQuery } from '@api/locations';
import { Location, LocationIncludeOption, PieceAction } from '@apiContract';
import sortBy from 'lodash/sortBy';

type Props = {
  value?: number | null;
  onLocationChange: (location: number | null) => void;
  sx?: BoxProps['sx'];
  disabled?: boolean;
  routeId?: number | null;
  showCount?: boolean;
};

type LocationOption = Location & { group?: string };

const AutocompleteLocation = forwardRef<HTMLDivElement, Props>(
  ({ disabled, value, onLocationChange, sx, routeId, showCount = false, ...restProps }, ref) => {
    const queryParams = useMemo(
      () => ({
        ...(showCount && {
          Include: [LocationIncludeOption.Pieces],
          'Filter.LifecycleStage': PieceAction.RequestDelivery,
        }),
        ...(routeId && { 'Filter.DeliveryRouteId': routeId }),
        'Paging.ItemsPerPage': 1000000,
        'Sorting.SortBy': 'Name',
      }),
      [showCount, routeId],
    );

    const { data: locationsData } = useGetLocationsApiListQuery(queryParams);

    const { data: route } = useGetDeliveryRouteByIdQuery(routeId || '', { skip: !routeId });

    const options: LocationOption[] = useMemo(() => {
      if (!locationsData?.items) return [];

      if (routeId && route?.locations) {
        const locationOrderMap = new Map(route.locations.map((loc, index) => [loc.id, index]));
        const groupName = route.name || '';

        return sortBy(locationsData.items, (loc) => locationOrderMap.get(loc.id) ?? Infinity).map((loc) => ({
          ...loc,
          group: groupName,
        }));
      }

      return locationsData.items;
    }, [locationsData?.items, route, routeId]);

    const selectedOption = useMemo(() => options.find((location) => location.id === value) ?? null, [options, value]);

    const handleChange = useCallback(
      (_: unknown, location: LocationOption | null) => {
        onLocationChange(location?.id ?? null);
      },
      [onLocationChange],
    );

    useEffect(() => {
      if (value != null && routeId && !selectedOption) {
        onLocationChange(null);
      }
    }, [value, routeId, selectedOption, onLocationChange]);

    const renderGroup = useMemo(
      () =>
        routeId
          ? (params: AutocompleteRenderGroupParams) => (
              <Box key={params.key}>
                <ListSubheader
                  component="div"
                  sx={{
                    px: 1,
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                  }}
                >
                  {params.group}
                </ListSubheader>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {params.children}
                </Box>
              </Box>
            )
          : undefined,
      [routeId],
    );

    const renderOption = useCallback(
      (liProps: HTMLAttributes<HTMLLIElement>, option: LocationOption) => {
        const { key, ...otherLiProps } = liProps as HTMLAttributes<HTMLLIElement> & { key: string };
        const count = option.piecesCount || 0;

        return (
          <Box component="li" key={key} {...otherLiProps}>
            <ListItemText primary={option.name} slotProps={{ primary: { sx: { fontWeight: 'normal' } } }} />
            {showCount && count != null && (
              <Box component="span" sx={{ ml: 1, color: 'text.secondary', flexShrink: 0 }}>
                {count}
              </Box>
            )}
          </Box>
        );
      },
      [showCount],
    );

    return (
      <Box sx={sx} ref={ref} {...restProps}>
        <Autocomplete
          value={selectedOption}
          fullWidth
          disabled={disabled}
          onChange={handleChange}
          options={options}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => <TextField {...params} label="Location" />}
          groupBy={routeId ? (option) => option.group || '' : undefined}
          renderGroup={renderGroup}
          renderOption={renderOption}
        />
      </Box>
    );
  },
);

AutocompleteLocation.displayName = 'AutocompleteLocation';

export default AutocompleteLocation;
